import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateBacktest, useStrategy } from '@/api/queries';
import { backtestSessions } from '@/api/backtestSessions';
import type { DatasetSummaryDto, StrategyDocumentDto, StrategySummaryDto } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { BacktestOperatorInputs, buildOperatorInputs, collectDeclaredOperatorInputs } from './BacktestOperatorInputs';
import styles from './NewBacktestDialog.module.css';
import { routes } from '@/lib/routePaths';

interface NewBacktestDialogProps {
  datasets: readonly DatasetSummaryDto[];
  strategies: readonly StrategySummaryDto[];
  fixedStrategyId?: string;
  fixedStrategyDocument?: StrategyDocumentDto;
  initialDatasetIds?: readonly string[];
  initialStrategyId?: string;
  onClose: () => void;
}

export function NewBacktestDialog({ datasets, strategies, fixedStrategyId, fixedStrategyDocument, initialDatasetIds, initialStrategyId, onClose }: NewBacktestDialogProps) {
  const navigate = useNavigate();
  const createBacktest = useCreateBacktest();
  const readyDatasets = datasets.filter((dataset) => dataset.status === 'Ready' && dataset.id);
  const prefill = resolveBacktestPrefill(datasets, strategies, initialDatasetIds, initialStrategyId, fixedStrategyId);
  const [datasetIds, setDatasetIds] = useState<string[]>(prefill.datasetIds);
  const [strategyId, setStrategyId] = useState(prefill.strategyId);
  const datasetTouched = useRef(false);
  const strategyTouched = useRef(false);
  const [accountSize, setAccountSize] = useState('10000');
  const [traceLevel, setTraceLevel] = useState('Summary');
  const [operatorInputs, setOperatorInputs] = useState<Record<string, unknown>>({});
  const [interactive, setInteractive] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const readyDatasetIds = new Set(readyDatasets.map((dataset) => dataset.id));
  const selectedDatasetIds = datasetIds.filter((id) => readyDatasetIds.has(id));
  const selectedStrategyId = fixedStrategyId ?? (strategies.some((strategy) => strategy.id === strategyId) ? strategyId : '');
  const { data: loadedStrategyDocument, isLoading: strategyLoading } = useStrategy(selectedStrategyId, {
    enabled: !!selectedStrategyId && !fixedStrategyDocument,
  });
  const strategyDocument = fixedStrategyDocument ?? loadedStrategyDocument;
  const declaredInputs = collectDeclaredOperatorInputs(strategyDocument);

  useEffect(() => {
    if (!datasetTouched.current) setDatasetIds(prefill.datasetIds);
    if (!strategyTouched.current) setStrategyId(prefill.strategyId);
  }, [prefill.datasetIds.join('|'), prefill.strategyId]);

  async function handleSubmit() {
    const parsedAccountSize = Number(accountSize);
    const validationError = validateBacktestForm(
      selectedDatasetIds,
      selectedStrategyId,
      strategyDocument,
      parsedAccountSize,
    );
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    const request = {
      datasetId: selectedDatasetIds[0],
      datasetIds: selectedDatasetIds,
      strategyDocument: strategyDocument!,
      accountSize: parsedAccountSize,
      traceLevel,
      operatorInputs: buildOperatorInputs(operatorInputs, declaredInputs),
    };
    try {
      if (interactive) {
        setLaunching(true);
        const session = await backtestSessions.create(request);
        if (!session.sessionId) throw new Error('The server did not return a session ID.');
        onClose();
        navigate(routes.backtestSession(session.sessionId));
        return;
      }

      const run = await createBacktest.mutateAsync(request);
      if (!run.id) throw new Error('The server did not return a run ID.');
      onClose();
      navigate(routes.backtest(run.id));
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Could not queue the backtest.');
    } finally {
      setLaunching(false);
    }
  }

  return (
    <Dialog
      title="New backtest"
      onClose={onClose}
      actions={(
        <>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            type="button"
            disabled={selectedDatasetIds.length === 0 || !selectedStrategyId || strategyLoading || createBacktest.isPending || launching}
            onClick={() => void handleSubmit()}
          >
            {launching
              ? 'Preparing replay…'
              : interactive ? 'Launch interactive replay' : 'Queue backtest'}
          </Button>
        </>
      )}
    >
      <div className={styles.fields}>
        <fieldset className={styles.datasetPicker}>
          <legend>Datasets</legend>
          {readyDatasets.map((dataset) => {
            const id = dataset.id ?? '';
            return (
              <label key={id}>
                <input
                  type="checkbox"
                  checked={selectedDatasetIds.includes(id)}
                  onChange={(event) => {
                    datasetTouched.current = true;
                    setDatasetIds((current) => event.target.checked ? [...current, id] : current.filter((item) => item !== id));
                  }}
                />
                <span>{dataset.displayName ?? id} <small>{dataset.instrument ?? 'unknown'}</small></span>
              </label>
            );
          })}
        </fieldset>
        {fixedStrategyDocument ? (
          <Input label="Strategy" value={fixedStrategyDocument.definition?.name ?? selectedStrategyId} disabled readOnly />
        ) : (
          <Select
            label="Strategy"
            value={selectedStrategyId}
            onChange={(event) => { strategyTouched.current = true; setStrategyId(event.target.value); }}
            options={[
              ...(!selectedStrategyId ? [{ value: '', label: 'Choose a saved strategy' }] : []),
              ...strategies.map((strategy) => ({ value: strategy.id, label: strategy.name })),
            ]}
          />
        )}
        <Input
          label="Starting account"
          type="number"
          min="0.01"
          step="0.01"
          value={accountSize}
          onChange={(event) => setAccountSize(event.target.value)}
        />
        <Select
          label="Trace detail"
          value={traceLevel}
          onChange={(event) => setTraceLevel(event.target.value)}
          options={[
            { value: 'Summary', label: 'Summary — decisions and trades' },
            { value: 'Full', label: 'Full — node context at decisions' },
          ]}
        />
        <BacktestOperatorInputs inputs={declaredInputs} values={operatorInputs} onChange={setOperatorInputs} />
        <label className={styles.interactiveToggle}>
          <input
            type="checkbox"
            checked={interactive}
            onChange={(event) => setInteractive(event.target.checked)}
          />
          <span>
            Launch in interactive playback
            <small className="statusText statusTextMuted">
              {' '}Replays the tape live: step bar by bar, flip operator inputs mid-run, then commit the steered timeline as a run.
            </small>
          </span>
        </label>
        {readyDatasets.length === 0 && (
          <p className="statusText statusTextWarning">Import a ready dataset before creating a backtest.</p>
        )}
        {strategies.length === 0 && (
          <p className="statusText statusTextWarning">Create a saved strategy before creating a backtest.</p>
        )}
        {prefill.invalidDataset && readyDatasets.length > 0 ? (
          <p className="statusText statusTextWarning">The requested dataset is unavailable or not ready. Choose a ready dataset.</p>
        ) : null}
        {prefill.invalidStrategy && strategies.length > 0 && (
          <p className="statusText statusTextWarning">The requested strategy is unavailable. Choose a saved strategy.</p>
        )}
        {error && <p className="statusText statusTextDanger">{error}</p>}
      </div>
    </Dialog>
  );
}

export function validateBacktestForm(
  datasetIds: string | readonly string[],
  strategyId: string,
  strategyDocument: StrategyDocumentDto | undefined,
  accountSize: number,
) {
  const hasDataset = typeof datasetIds === 'string' ? !!datasetIds : datasetIds.length > 0;
  if (!hasDataset || !strategyId || !strategyDocument) {
    return 'Choose a ready dataset and saved strategy first.';
  }
  if (!Number.isFinite(accountSize) || accountSize <= 0) {
    return 'Account size must be greater than zero.';
  }
  return null;
}

export function resolveBacktestPrefill(
  datasets: readonly DatasetSummaryDto[],
  strategies: readonly StrategySummaryDto[],
  initialDatasetIds?: readonly string[],
  initialStrategyId?: string,
  fixedStrategyId?: string,
) {
  const readyIds = new Set(datasets.filter((dataset) => dataset.status === 'Ready' && dataset.id).map((dataset) => dataset.id!));
  const requestedDatasets = initialDatasetIds?.length ? initialDatasetIds.filter((id) => readyIds.has(id)) : undefined;
  const firstReadyId = datasets.find((dataset) => dataset.status === 'Ready' && dataset.id)?.id;
  const strategyIds = new Set(strategies.map((strategy) => strategy.id));
  return {
    datasetIds: requestedDatasets ?? (firstReadyId ? [firstReadyId] : []),
    strategyId: fixedStrategyId ?? (initialStrategyId
      ? strategyIds.has(initialStrategyId) ? initialStrategyId : ''
      : strategies[0]?.id ?? ''),
    invalidDataset: !!initialDatasetIds?.length && requestedDatasets?.length === 0,
    invalidStrategy: !!initialStrategyId && !strategyIds.has(initialStrategyId),
  };
}
